// 文件传输 ZIP 打包下载：流式写盘 + 直连失败走 Worker proxy + 部分失败继续。
import { Zip, ZipPassThrough } from "fflate";
import streamSaver from "streamsaver";

let streamSaverConfigured = false;

const MAX_FETCH_ATTEMPTS = 3;
const RETRY_DELAY_MS = 400;
const FAILED_FILES_REPORT_NAME = "_download-failures.txt";

export const CANCELLED_MESSAGE = "CANCELLED";
export const UNSUPPORTED_MESSAGE = "STREAMING_ZIP_UNSUPPORTED";

function getPublicUrlPrefix() {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  if (!base || base === ".") return "";
  return base;
}

function configureStreamSaver() {
  if (streamSaverConfigured) return;
  streamSaver.mitm = `${window.location.origin}${getPublicUrlPrefix()}/mitm.html`;
  streamSaverConfigured = true;
}

// streamSaver 在 Safari / 无 Service Worker 等场景会静默退回内存 Blob，
// 整包进内存会把页面卡死，这里要求必须是真正的流式写盘环境。
export function isStreamingZipSupported() {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext !== true) return false;
  if (
    typeof ReadableStream === "undefined" ||
    typeof WritableStream === "undefined" ||
    typeof TransformStream === "undefined" ||
    typeof MessageChannel === "undefined"
  ) {
    return false;
  }
  if (!navigator.serviceWorker) return false;
  if (window.safari || window.WebKitPoint) return false;
  return true;
}

export function safeZipFilename(name) {
  const cleaned = String(name || "files")
    .replace(/[^\w\u4e00-\u9fff.\- ()]+/g, "_")
    .trim();
  return `${(cleaned || "files").slice(0, 80)}.zip`;
}

export function uniqueZipPath(path, usedPaths) {
  const base = String(path || "file");
  if (!usedPaths.has(base)) {
    usedPaths.add(base);
    return base;
  }
  const dotIdx = base.lastIndexOf(".");
  const stem = dotIdx > 0 ? base.slice(0, dotIdx) : base;
  const ext = dotIdx > 0 ? base.slice(dotIdx) : "";
  let index = 2;
  let candidate = `${stem} (${index})${ext}`;
  while (usedPaths.has(candidate)) {
    index += 1;
    candidate = `${stem} (${index})${ext}`;
  }
  usedPaths.add(candidate);
  return candidate;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function formatFailureReport(failed, zipName) {
  const lines = [
    `Failed files in "${zipName || "files"}": ${failed.length}`,
    `Generated: ${new Date().toISOString()}`,
    "",
  ];
  failed.forEach((item, index) => {
    lines.push(`${index + 1}. ${item.name || "unknown"}`);
    lines.push(`   ${item.error || "Download failed"}`);
  });
  return `${lines.join("\n")}\n`;
}

function addStringToZip(zip, zipPath, text) {
  const entry = new ZipPassThrough(zipPath);
  zip.add(entry);
  entry.push(new TextEncoder().encode(text), true);
}

async function fetchDirectThenProxy(primaryUrl, proxyUrl, signal) {
  if (!primaryUrl && !proxyUrl) throw new Error("Missing download URL");
  if (!primaryUrl) return fetch(proxyUrl, { cache: "no-store", signal });
  try {
    return await fetch(primaryUrl, { cache: "no-store", signal });
  } catch (error) {
    if (!proxyUrl) throw error;
    console.warn("[file-transfer-download] direct R2 fetch failed, falling back to proxy", {
      error: error.message,
    });
    return fetch(proxyUrl, { cache: "no-store", signal });
  }
}

async function fetchFileResponse(downloadUrl, proxyDownloadUrl, signal) {
  const response = await fetchDirectThenProxy(downloadUrl, proxyDownloadUrl, signal);
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ""}`);
    error.status = response.status;
    throw error;
  }
  return response;
}

async function fetchFileResponseWithRetry(downloadUrl, proxyDownloadUrl, signal) {
  let lastError = null;
  for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt += 1) {
    if (signal?.aborted) throw new Error(CANCELLED_MESSAGE);
    try {
      return await fetchFileResponse(downloadUrl, proxyDownloadUrl, signal);
    } catch (error) {
      lastError = error;
      if (attempt < MAX_FETCH_ATTEMPTS) await delay(RETRY_DELAY_MS * attempt);
    }
  }
  throw lastError || new Error("Download failed");
}

async function pumpResponseToZip(response, zipEntry, signal, checkFatal) {
  if (!response.body) {
    checkFatal?.();
    const buffer = new Uint8Array(await response.arrayBuffer());
    checkFatal?.();
    zipEntry.push(buffer, true);
    checkFatal?.();
    return;
  }

  const reader = response.body.getReader();
  try {
    while (true) {
      if (signal?.aborted) throw new Error(CANCELLED_MESSAGE);
      checkFatal?.();
      const { done, value } = await reader.read();
      if (done) {
        zipEntry.push(new Uint8Array(0), true);
        break;
      }
      zipEntry.push(value, false);
      checkFatal?.();
    }
  } finally {
    reader.releaseLock();
  }
}

async function addFileToZip(zip, zipPath, downloadUrl, proxyDownloadUrl, signal, checkFatal) {
  checkFatal?.();
  const response = await fetchFileResponseWithRetry(downloadUrl, proxyDownloadUrl, signal);
  checkFatal?.();
  const entry = new ZipPassThrough(zipPath);
  zip.add(entry);
  try {
    await pumpResponseToZip(response, entry, signal, checkFatal);
  } catch (error) {
    // 响应已经开始写入 ZIP 后失败，无法安全移除该 entry；
    // 继续往同一个归档添加文件会生成损坏的 ZIP，这里标记为致命错误。
    error.fatalArchive = true;
    throw error;
  }
}

/**
 * 批量打包下载所有文件为 ZIP。
 *
 * @param {Array} files - [{ originalName, downloadUrl, proxyDownloadUrl }]
 * @param {string} zipName
 * @param {{ onProgress, signal }} options
 * @returns {Promise<{ succeeded: number, failed: Array, total: number }>}
 */
export async function downloadAllAsZip(files, zipName = "files", { onProgress, signal } = {}) {
  if (!files?.length) throw new Error("NO_FILES");
  if (!isStreamingZipSupported()) throw new Error(UNSUPPORTED_MESSAGE);

  configureStreamSaver();
  const fileStream = streamSaver.createWriteStream(safeZipFilename(zipName));
  const writer = fileStream.getWriter();

  let writeQueue = Promise.resolve();
  let zipError = null;
  let writeError = null;

  const recordWriteError = (error) => {
    if (!writeError) writeError = error;
    throw error;
  };

  const checkFatal = () => {
    if (zipError || writeError) throw zipError || writeError;
  };

  const zip = new Zip((err, chunk, final) => {
    if (err) {
      zipError = err;
      return;
    }
    if (chunk?.length) {
      writeQueue = writeQueue.then(() => writer.write(chunk)).catch(recordWriteError);
    }
    if (final) {
      writeQueue = writeQueue.then(() => writer.close()).catch(recordWriteError);
    }
  });

  const usedPaths = new Set();
  const failed = [];
  let succeeded = 0;
  const total = files.length;

  try {
    for (let i = 0; i < total; i += 1) {
      if (signal?.aborted) throw new Error(CANCELLED_MESSAGE);
      checkFatal();

      const file = files[i];
      const zipPath = uniqueZipPath(file.originalName || `file-${i + 1}`, usedPaths);
      onProgress?.({
        current: i + 1,
        total,
        name: file.originalName || "",
        failedCount: failed.length,
        phase: "fetching",
      });

      try {
        await addFileToZip(zip, zipPath, file.downloadUrl, file.proxyDownloadUrl, signal, checkFatal);
        checkFatal();
        await writeQueue;
        checkFatal();
        succeeded += 1;
      } catch (error) {
        if (signal?.aborted) throw error;
        if (zipError || writeError || error.fatalArchive) throw zipError || writeError || error;
        failed.push({
          name: file.originalName || zipPath,
          error: error.message || "Download failed",
        });
        onProgress?.({
          current: i + 1,
          total,
          name: file.originalName || "",
          failedCount: failed.length,
          phase: "failed",
        });
      }
    }

    if (failed.length) {
      addStringToZip(zip, FAILED_FILES_REPORT_NAME, formatFailureReport(failed, zipName));
      checkFatal();
      await writeQueue;
      checkFatal();
    }

    zip.end();
    await writeQueue;
    checkFatal();

    onProgress?.({
      current: total,
      total,
      name: "",
      done: true,
      failedCount: failed.length,
      succeededCount: succeeded,
    });

    return { succeeded, failed, total };
  } catch (error) {
    zip.terminate();
    try {
      await writer.abort(error);
    } catch {
      /* ignore */
    }
    throw error;
  }
}
